import { getStoredSession } from '../utils/sessionManager';
import authService from './auth.service';

class ApiService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_RENDERER_API_URL
      ? `${process.env.REACT_APP_RENDERER_API_URL}/v1`
      : 'https://dev-opus-renderer.aelive.cloud/v1';
    // Scene-service base needs the /v1 prefix (the actual route lives at
    // /v1/scenes/organization/:id; the unversioned path 404s with "Cannot
    // GET /scenes/..."). Aedrive's REACT_APP_SCENE_API_URL convention
    // omits /v1; Aero dashboard's env bakes it in. Normalize either way
    // so we end up with exactly one /v1 at the end.
    this.sceneBaseUrl = process.env.REACT_APP_SCENE_API_URL
      ? `${process.env.REACT_APP_SCENE_API_URL.replace(/\/+$/, "").replace(/\/v1$/, "")}/v1`
      : "";
  }

  async request(endpoint, options = {}) {
    const { baseUrl, ...rest } = options;
    const url = `${baseUrl || this.baseUrl}${endpoint}`;
    options = rest;

    // Ensure token is valid before making request (auto-refresh if needed)
    if (authService.isAuthenticated() &&
      !endpoint.includes('/login') &&
      !endpoint.includes('/refresh-token')) {
      await authService.ensureTokenValid();
    }

    const headers = {
      'Accept': 'application/json',
      ...options.headers
    };

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const session = getStoredSession();
    if (session?.accessKey) {
      headers.Authorization = `Bearer ${session.accessKey}`;
    }

    const requestOptions = {
      ...options,
      headers,
      mode: 'cors'
    };

    const response = await fetch(url, requestOptions);

    // Handle token expiration with automatic refresh
    if (response.status === 401 || response.status === 403) {
      console.log(`Token expired for request to ${endpoint}, attempting refresh...`);

      // Token might be expired, try to refresh
      const refreshed = await authService.refreshToken();
      if (refreshed) {
        console.log('Token refreshed successfully, retrying request...');

        // Retry the request with new token
        const newSession = getStoredSession();
        if (newSession?.accessKey) {
          headers.Authorization = `Bearer ${newSession.accessKey}`;
        }

        const retryResponse = await fetch(url, {
          ...options,
          headers,
          mode: 'cors'
        });

        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          throw new Error(`HTTP ${retryResponse.status}: ${retryResponse.statusText} - ${errorText}`);
        }

        return retryResponse.json();
      } else {
        console.error('Token refresh failed');
        throw new Error('Authentication failed - please refresh the page');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  // Scene-service fetch — separate base URL, scene service is not on the
  // renderer-api host. Mirrors Aero dashboard's getScenesByOrganization
  // so renderer pages can resolve scene names from scene_id the same way.
  async getScenesByOrganization(orgId) {
    if (!this.sceneBaseUrl) return { scenes: [] };
    return this.request(
      `/scenes/organization/${orgId}?includeOrg=true&includeUsers=true`,
      { baseUrl: this.sceneBaseUrl },
    );
  }

  /**
   * One of the organisation's scenes, by its exact name, or null when it has
   * none. A failed read throws, so callers can tell "no such scene" from a
   * network blip. Asks for the bare list: the org and user joins that
   * getScenesByOrganization requests are not needed here.
   */
  async getSceneByName(orgId, name) {
    if (!orgId || !name || !this.sceneBaseUrl) return null;
    const res = await this.request(`/scenes/organization/${orgId}`, { baseUrl: this.sceneBaseUrl });
    const scenes = res?.data?.scenes || res?.scenes || res?.data || [];
    return (Array.isArray(scenes) ? scenes : []).find((s) => s?.name === name) || null;
  }

  async getRenderersByOrganization(orgId) {
    // Fetch renderers + scenes in parallel and join via scene_id so each
    // renderer's scene.name resolves even when the renderer API doesn't
    // embed allocation_details.scene (which is the case for most rows
    // returned from /renderers/organization). Mirrors the dashboard's
    // RendererManagement pattern (createSceneLookupMap + merge).
    const [renderersRes, scenesRes] = await Promise.all([
      this.request(`/renderers/organization/${orgId}`),
      this.getScenesByOrganization(orgId).catch((err) => {
        console.warn("[apiService] scene fetch failed; renderer rows will lack scene.name", err);
        return { scenes: [] };
      }),
    ]);
    const data = renderersRes?.data || renderersRes;
    const scenes =
      scenesRes?.data?.scenes || scenesRes?.scenes || scenesRes?.data || scenesRes || [];
    const sceneMap = new Map();
    if (Array.isArray(scenes)) {
      for (const s of scenes) {
        if (s?.id) sceneMap.set(s.id, s);
      }
    }
    if (data?.renderers && Array.isArray(data.renderers)) {
      data.renderers = data.renderers.map((r) => {
        const nestedScene = r.allocation_details?.scene || {};
        // The scene id is NOT always a top-level field. /renderers/organization
        // returns it nested under allocation_details.scene.id and omits
        // r.scene_id entirely, so keying the lookup on r.scene_id alone left
        // lookupScene null for every row that HAS an allocation — which is
        // exactly the rows that need it. Fall back to the nested id.
        const sceneKey = r.scene_id || nestedScene.id;
        const lookupScene = sceneKey ? sceneMap.get(sceneKey) : null;
        return {
          ...r,
          scene: {
            id: nestedScene.id || r.scene_id || lookupScene?.id,
            // Resolve scene name from (in priority order): the renderer's
            // nested allocation, a top-level scene_name field, the scenes
            // lookup. Never the renderer's own name — that would put the
            // renderer in its own fake scene under the Scene dropdown.
            name: nestedScene.name || r.scene_name || lookupScene?.name,
            path: nestedScene.path || r.scene_path || lookupScene?.scene_path,
            // Scene ASSET folders. Carried through so team badges (and, later,
            // player photos) resolve from the scene the graphics actually
            // render with, instead of a single bucket baked in at build time.
            // Only the scenes lookup has these — the renderer's nested
            // allocation does not return them.
            load_team_badge_path:
              nestedScene.load_team_badge_path ?? lookupScene?.load_team_badge_path ?? null,
            load_player_path:
              nestedScene.load_player_path ?? lookupScene?.load_player_path ?? null,
          },
        };
      });
    }
    return data;
  }

  async getRendererById(rendererId) {
    const res = await this.request(`/renderers/${rendererId}`);
    return res?.data || res;
  }

  /**
   * Fetch a scene's asset folders (badge/player/part) by id.
   *
   * The renderer API's nested allocation carries only id/name/path/version/
   * captions — the asset folders live on the scene record and are only ever
   * returned by scene-service. Kept separate so callers can fail soft: a scene
   * we cannot read must degrade to the env-var badge bucket, never throw.
   */
  async getSceneAssetPaths(sceneId) {
    if (!sceneId || !this.sceneBaseUrl) return null;
    try {
      const res = await this.request(`/scenes/${sceneId}`, { baseUrl: this.sceneBaseUrl });
      const s = res?.data?.scene || res?.data || res;
      if (!s) return null;
      return {
        load_team_badge_path: s.load_team_badge_path ?? null,
        load_player_path: s.load_player_path ?? null,
      };
    } catch (err) {
      console.warn("[apiService] scene asset-path lookup failed; badges fall back to the env bucket", err);
      return null;
    }
  }

  async getRendererDetails(rendererId) {
    const res = await this.request(`/renderers/${rendererId}/details?attachBothSceneCaption=true`);
    const data = res?.data || res;
    if (data) {
      // Map nested backend v1 structures back to root-level format expected by aedrive-ui
      const sceneData = data.allocation_details?.scene;
      if (sceneData) {
        data.scene_path = sceneData.path;
        data.captions = sceneData.captions;
        data.scene = sceneData;

        // The renderer API does not return the scene's asset folders — verified
        // against PLS, where allocation_details.scene is exactly
        // {id,name,path,version,captions}. Without this second read
        // scene.load_team_badge_path is undefined for the SELECTED renderer, so
        // resolveTeamBadgeBase() returns null and every badge silently falls
        // back to REACT_APP_S3_TEAM_BADGES — the single build-time bucket that
        // OPUS-1489 exists to stop depending on.
        if (sceneData.load_team_badge_path == null) {
          const assets = await this.getSceneAssetPaths(sceneData.id);
          if (assets) {
            data.scene = { ...sceneData, ...assets };
          }
        }
      }
    }
    return data;
  }
}

const apiService = new ApiService();
export default apiService;

